import styled, { useTheme } from 'styled-components'
import { match } from 'ts-pattern'
import { ErrorResponseDto } from '@/apis/rest/generated/types'
import Modal from '@/components/common/modals/Modal'
import { Typo, TypoVariant } from '@/components/common/typography'
import ROUTES from '@/constants/legacy/constRoutes'
import { useCustomRouter } from '@/containers/hooks'
import { ErrorType } from '@/plugins/rest-api-v4'

interface OrderErrorModalsProps {
  error?: ErrorType<ErrorResponseDto>
  isOpen: boolean
  onClick: () => void
}

export const OrderErrorModals = ({ error, isOpen, onClick }: OrderErrorModalsProps) => {
  if (!error) {
    return null
  }

  const { replace } = useCustomRouter()
  const { color } = useTheme()

  const errorType = error.response?.data.errors[0].subCode

  const handleOkClick = (path: string) => {
    if (!path) {
      onClick()
      return
    }
    replace(path)
  }

  const modalContents = match(errorType)
    .with('INSUFFICIENT_STOCK', () => {
      return {
        title: '장바구니에 담은 상품 중\n품절된 상품이 있어요',
        subTitle: '품절된 상품을 삭제하신 후 결제해 주세요',
        contentBody: error.response?.data.errors[0].parameterValue,
        okButtonText: '장바구니로 이동',
        replacePath: ROUTES.CART,
      }
    })
    .with('COUPON_UNDER_MINIMUM_AMOUNT', () => {
      return {
        title: '상품쿠폰을 적용할 수 없어요',
        subTitle: '상품쿠폰으로 더 할인받을 경우 현재 적용된\n주문쿠폰의 최소주문금액이 미달돼요',
        contentBody: '',
        okButtonText: '확인',
        replacePath: '',
      }
    })
    .with('NOT_APPROVED_PRODUCT', () => {
      return {
        title: '구매할 수 없는 상품이에요',
        subTitle: '해당 상품은 구매가 제한된 상품이에요',
        contentBody: error.response?.data.errors[0].parameterValue,
        okButtonText: '장바구니로 이동',
        replacePath: ROUTES.CART,
      }
    })
    .otherwise(() => {
      return {
        title: '결제 정보를 불러오지 못했어요',
        subTitle: '같은 문제가 반복된다면 장바구니에 담긴 상품을\n전체 삭제하고 다시 담아주세요',
        contentBody: '',
        okButtonText: '확인',
        replacePath: '',
      }
    })

  return (
    <Modal open={isOpen}>
      <Modal.Content>
        <Modal.Title>
          <div>
            <Typo variant={TypoVariant.Heading6Bold} color={color.gray900} style={{ whiteSpace: 'pre-wrap' }}>
              {modalContents.title}
            </Typo>
          </div>
        </Modal.Title>
        <Modal.SubTitle>
          <Typo variant={TypoVariant.Body3Regular} color={color.gray700} style={{ whiteSpace: 'pre-wrap' }}>
            {modalContents.subTitle}
          </Typo>
        </Modal.SubTitle>
        {modalContents.contentBody && (
          <StyledContentBody>
            <Typo variant={TypoVariant.Body3Regular} color={color.gray700} style={{ whiteSpace: 'pre-wrap' }}>
              {modalContents.contentBody}
            </Typo>
          </StyledContentBody>
        )}
      </Modal.Content>
      <Modal.Footer style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
        <StyledModalButton className="ok" onClick={() => handleOkClick(modalContents.replacePath)}>
          <Typo variant={TypoVariant.Body1Bold} color={color.grayWhite}>
            {modalContents.okButtonText}
          </Typo>
        </StyledModalButton>
      </Modal.Footer>
    </Modal>
  )
}

const StyledModalButton = styled.button`
  flex: 1;
  border-radius: 0.8rem;
  border: 0.1rem solid;
  padding: 1.3rem 1.6rem;
  cursor: pointer;
  width: 100%;

  &.ok {
    border-color: ${({ theme }) => theme.color.blue500};
    background-color: ${({ theme }) => theme.color.blue500};
  }
`

const StyledContentBody = styled.div`
  margin-top: 1.6rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1.6rem;
  border-radius: 0.8rem;
  background-color: ${({ theme }) => theme.color.gray50};
`
